import { HistoryCreatedInfo } from "@/features/history/HistoryCreatedInfo";
import { getHistoryIcon } from "@/features/history/activityIcons";
import { isDropinTask } from "@/features/tasks/isDropinTask";
import { getTaskUpdateDetail } from "@/features/tasks/utils";
import { type History, type Task } from "@repo/api";
import { ListView, ListViewItem, Text, calendarIcon } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import { type TaskConfig, getTaskIcon, useSettings } from "@repo/ui";
import { type Locale } from "date-fns";
import isEmpty from "lodash/isEmpty";
import { type ReactElement } from "react";

interface TaskHistory {
  title: string;
  description?: string | ReactElement;
  createdBy: string;
  createdAt: string;
  icon: any;
}

const normalizeHistories = (
  language: string,
  entries?: History[],
  dateFnsLocale?: Locale,
  taskConfigs?: TaskConfig[]
) => {
  if (!entries) {
    return [];
  }
  return (
    entries.map((entry) => ({
      createdAt: entry.createdAt,
      createdBy: entry.createdBy,
      title:
        getTaskUpdateDetail(language, entry, dateFnsLocale, taskConfigs)
          ?.title ?? entry.title,
      icon: getHistoryIcon(
        entry.entryType,
        entry.details?.parent_task_type,
        entry.details?.sub_task_type,
        taskConfigs
      ),
      description: (
        <>
          {getTaskUpdateDetail(
            language,
            entry,
            dateFnsLocale,
            taskConfigs
          )?.description?.map((el, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Text key={i} textStyle="Body.S">
              {el}
            </Text>
          ))}
        </>
      ),
    })) ?? []
  );
};

const getTaskHistories = (
  language: string,
  task?: Task,
  historyEntries?: History[],
  dateFnsLocale?: Locale,
  taskConfigs?: TaskConfig[]
) => {
  if (!task && !historyEntries) {
    return [];
  }
  const allEntries = [] as TaskHistory[];
  const isDropIn = isDropinTask(task?.parentTaskType, task?.subTaskType);
  // Add task creation log
  if (
    task?.created?.createdBy !== undefined &&
    task.created.taskCreatedDateTime !== undefined
  ) {
    allEntries.push({
      createdAt: task.created.taskCreatedDateTime,
      createdBy: task.created.createdBy,
      title: isDropIn
        ? t("prompt.appointment_created")
        : t("prompt.task_created"),
      icon: isDropIn
        ? calendarIcon
        : getTaskIcon(task.parentTaskType, task.subTaskType, taskConfigs),
    });
  }

  return allEntries
    .concat(
      normalizeHistories(language, historyEntries, dateFnsLocale, taskConfigs)
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
};

export const TaskHistories = ({
  task,
  historyEntries,
}: {
  task?: Task;
  historyEntries?: History[];
}) => {
  const { dateFnsLocale } = useTranslation();
  const { language, task_configuration } = useSettings();
  const allEntries = getTaskHistories(
    language,
    task,
    historyEntries,
    dateFnsLocale,
    task_configuration?.task_types
  );

  if (isEmpty(allEntries)) {
    return null;
  }
  return (
    <>
      <ListView id="task-history-list">
        {allEntries.map((entry, index) => (
          <ListViewItem
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            title={entry.title}
            description={entry.description}
            leadingIcon={entry.icon}
            addOn={
              <HistoryCreatedInfo
                createdBy={entry.createdBy}
                createdAt={entry.createdAt}
              />
            }
          />
        ))}
      </ListView>
    </>
  );
};
